import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import Select from "@/components/atoms/Select";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import { CardDetailsProps } from "@/interfaces/Card";
import { Team, TeamUpdateForm, TeamUpdateFormSend } from "@/interfaces/Team";
import { ISelectOption } from "@/interfaces/Select";
import { useGetTeamQuery, useUpdateTeamMutation } from "@/services/teams";
import { useGetTeamUsersQuery } from "@/services/teams/users";
import { useGetUsersQuery } from "@/services/users";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { FC, useEffect, useMemo } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";

const TeamEditCard: FC<CardDetailsProps<Team>> = ({ id }) => {
  const navigation = useNavigate();
  const { data, isLoading, isError, error } = useGetTeamQuery(id);
  const [updateTeamAsync] = useUpdateTeamMutation();

  const { data: managersData } = useGetTeamUsersQuery([
    id,
    { pageIndex: 0, pageSize: 200, isManager: true },
  ]);

  const { data: usersData } = useGetUsersQuery({
    pageIndex: 0,
    pageSize: 1000,
    sortBy: "firstname",
    sortDirection: "asc",
  });

  const userOptions = useMemo<ISelectOption<string>[]>(
    () =>
      usersData?.data?.map((user) => ({
        value: user.id,
        label: `${user.firstname} ${user.lastname} (${user.email})`,
      })) || [],
    [usersData],
  );

  const managerOptions = useMemo<ISelectOption<string>[]>(
    () =>
      managersData?.data?.map((teamUser) => ({
        value: teamUser.userId,
        label: `${teamUser.user.firstname} ${teamUser.user.lastname} (${teamUser.user.email})`,
      })) || [],
    [managersData],
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TeamUpdateForm>();

  useEffect(() => {
    data &&
      reset({
        name: data.name,
        description: data.description,
        color: data.color,
        fullVisibility: data.fullVisibility,
        managers: managerOptions,
      });
  }, [data, managerOptions, reset]);

  const onSubmit: SubmitHandler<TeamUpdateForm> = async ({
    managers,
    description,
    color,
    fullVisibility,
    ...newData
  }) => {
    const result = getDifferenceBetweenTwoObjects<TeamUpdateForm>(
      data,
      newData,
    );

    const { managers: _ignoredManagers, ...rest } = result as any;

    const payload: TeamUpdateFormSend = {
      ...rest,
      description: description ?? null,
      color: color ?? null,
      fullVisibility: fullVisibility ?? false,
      ...(managers && { managerIds: managers.map((m) => m.value) }),
    };

    await toast.promise(updateTeamAsync([id, payload]).unwrap(), {
      pending: "Modification de l'équipe en cours ...",
      success: {
        render() {
          navigation(-1);
          return (
            <p className="mb-0 text-center">
              L&apos;équipe a bien été modifiée
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<TeamUpdateForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <Card>
        <CardHeader title="Modification de l'équipe" />
        <Card.Body>
          <Row className="gx-3">
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nom <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : Équipe Nord"
                  type="text"
                  {...register("name", {
                    required: INVALID_NAME_MESSAGE,
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Couleur :</Form.Label>
                <Form.Control
                  type="color"
                  {...register("color")}
                  isInvalid={!!errors.color}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.color?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description :</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  {...register("description", {
                    maxLength: {
                      value: 2000,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>Managers :</Form.Label>
                <Controller
                  control={control}
                  name="managers"
                  rules={{
                    validate: (v) =>
                      (v && v.length > 0) || INVALID_FORM_MESSAGE,
                  }}
                  render={({ field }) => (
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      options={userOptions}
                      value={field.value ?? []}
                      onChange={(v) => field.onChange(v)}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.managers?.message as string | undefined}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="edit-full-visibility"
                  label="Autoriser le chef d'équipe à voir toutes les fiches"
                  {...register("fullVisibility")}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button variant="primary" type="submit">
                Enregistrer
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default TeamEditCard;
